import { FC } from 'react';
import { useIntl } from 'react-intl';
import { Button, createBemBlockBuilder, Hyperlink } from '@ebsco-ui/ebsco-ui';

import { Modal, ModalHeader } from '@app/components';
import { sharedMessages } from '@app/translations';

import './LegalStatementsModal.scss';

interface LegalStatementsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const cnBem = createBemBlockBuilder(['legalStatementsModal']);

export const cardPermissionFormLink =
  'https://reader-registration.loc.gov/readerreg/docs/ReaderRegistrationforMinors.pdf';

export const LegalStatementsModal: FC<LegalStatementsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { $t } = useIntl();

  const legalStatementsTitle = $t({
    id: 'preregistration.legalStatements',
    defaultMessage: 'Legal Statements',
  });

  const legalStatementsBody = (
    <>
      {$t(
        {
          id: 'preregistration.agreementLegalStatementModalText',
          defaultMessage:
            "Users of the Library's research areas, including Computer Catalog Centers, and Copyright Office public service areas are each required to have a Reader Identification Card issued by the Library.{br} Cards are free and can be obtained by completing a registration process and presenting a valid government-issued ID, for example a driver's license, state-issued identification card, or passport. Researchers 16 and 17 years of age must also supply a <link>Library of Congress Reader Identification Card Permission Form-Minor Teenagers</link> and bring the signed document with them to obtain a Reader Identification Card.{br} Use this system to pre-register for your Library of Congress Reader Identification Card. In order to complete your registration and receive your card, you must come to the Library of Congress in person and present your valid form of identification at the Reader Registration Station located in the Madison Building, Room LM 133 (in the Newspaper & Current Periodicals Reading Room) or the Jefferson Building, Room LJ 139 (in the Microform and Electronic Resources Center).{br} I acknowledge that the use of the Library of Congress reading rooms, collections, and facilities is a privilege extended to the public by the Congress of the United States. By accepting the Reader Identification Card and the entitlements thereunder, I agree to comply with, and be bound by all Library of Congress rules and regulations governing the use of Library collections, services, systems, facilities, reading rooms, and other premises of the Library.{br} The Copyright law of the United States governs the use of copyrighted materials. Without the consent of the copyright owner, Library patrons may not copy or otherwise use copyrighted materials in a manner protected by law, except as permitted by fair use (17 U.S.C. § 107) or other copyright law. The Library of Congress can neither grant nor deny permission to use materials in its collection. Library patrons are personally liable for any copyright infringement they commit while using Library facilities. Additionally, Library patrons are responsible for complying with all privacy requirements.{br} The Library asks that researchers approach the materials in our collections with respect for the culture and sensibilities of the people whose lives, ideas, and creativity are documented here.{br}",
        },
        {
          br: <br />,
          link: chunks => (
            <Hyperlink
              href={cardPermissionFormLink}
              target="_blank"
              rel="noreferrer"
            >
              {chunks}
            </Hyperlink>
          ),
        }
      )}
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      modalTitle={legalStatementsTitle}
      className={cnBem('__legalModal')}
      onClose={onClose}
    >
      <ModalHeader
        titleLevel={2}
        title={legalStatementsTitle}
        className={cnBem('__header')}
        onClose={onClose}
      />
      <div className={cnBem('__container')}>
        <Modal.Body className={cnBem('__body')}>
          {legalStatementsBody}
        </Modal.Body>
      </div>
      <Modal.Footer className={cnBem('__footer')}>
        <Button
          title={$t(sharedMessages.close)}
          styleType="text"
          onClick={onClose}
        >
          {$t(sharedMessages.close)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
